import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { sendChatMessage } from '../../api/ai';
import type { ChatMessage } from '../../api/ai';
import { useEstimationStore } from '../../stores/estimationStore';
import { useEstimation } from '../../hooks/useEstimation';
import styles from './ChatPanel.module.css';

interface Props {
  projectId: string;
}

const SUGGESTED_QUESTION_KEYS = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6'] as const;

export function ChatPanel({ projectId }: Props) {
  const { t } = useTranslation('ai');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [toolCalls, setToolCalls] = useState<Record<string, unknown>[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const ha = useEstimationStore((s) => s.ha);
  const haZi = useEstimationStore((s) => s.haZi);
  const niveau = useEstimationStore((s) => s.niveau);
  const estimation = useEstimation();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function handleSend(text?: string) {
    const msg = text ?? input.trim();
    if (!msg || loading) return;

    const userMsg: ChatMessage = { role: 'user', content: msg };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const result = await sendChatMessage({
        session_id: projectId,
        message: msg,
        history: messages,
        ha,
        ha_zi: haZi,
        niveau,
        current_results: estimation as unknown as Record<string, unknown>,
      });

      const assistantMsg: ChatMessage = { role: 'assistant', content: result.reply };
      setMessages((prev) => [...prev, assistantMsg]);
      if (result.tool_calls.length > 0) {
        setToolCalls(result.tool_calls);
      }
    } catch {
      const errorMsg: ChatMessage = {
        role: 'assistant',
        content: t('ai:errorOccurred'),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  if (collapsed) {
    return (
      <button className={styles.floatingBtn} onClick={() => setCollapsed(false)}>
        <span className={styles.chatIcon}>&#x2728;</span>
        <span>{t('ai:chatTitle')}</span>
      </button>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerIcon}>&#x2728;</span>
          <span className={styles.headerTitle}>{t('ai:chatTitle')}</span>
        </div>
        <button className={styles.closeBtn} onClick={() => setCollapsed(true)}>
          &#x2715;
        </button>
      </div>

      <div className={styles.messagesArea}>
        {messages.length === 0 && (
          <div className={styles.welcome}>
            <h4 className={styles.welcomeTitle}>{t('ai:chatIntroAsk')}</h4>
            <p className={styles.welcomeText}>
              {t('ai:chatIntro')}
            </p>
            <div className={styles.suggestions}>
              {SUGGESTED_QUESTION_KEYS.map((k) => (
                <button
                  key={k}
                  className={styles.suggestionBtn}
                  onClick={() => handleSend(t(`ai:${k}`))}
                >
                  {t(`ai:${k}`)}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            className={`${styles.message} ${msg.role === 'user' ? styles.userMsg : styles.assistantMsg}`}
          >
            {msg.role === 'assistant' && (
              <span className={styles.msgIcon}>&#x2728;</span>
            )}
            <div className={styles.msgContent}>
              {msg.content.split('\n').map((line, j) => (
                <p key={j} className={styles.msgLine}>{line}</p>
              ))}
            </div>
          </div>
        ))}

        {loading && (
          <div className={`${styles.message} ${styles.assistantMsg}`}>
            <span className={styles.msgIcon}>&#x2728;</span>
            <div className={styles.thinkingDots}>
              <span /><span /><span />
            </div>
          </div>
        )}

        {toolCalls.length > 0 && !loading && (
          <div className={styles.toolCallsInfo}>
            <details>
              <summary className={styles.toolCallsSummary}>
                {t('ai:toolsUsed', { count: toolCalls.length })}
              </summary>
              <div className={styles.toolCallsList}>
                {toolCalls.map((tc, i) => (
                  <div key={i} className={styles.toolCallItem}>
                    <span className={styles.toolName}>{tc.tool as string}</span>
                    <code className={styles.toolInput}>
                      {JSON.stringify(tc.input, null, 0).slice(0, 80)}
                    </code>
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className={styles.inputArea}>
        <textarea
          ref={inputRef}
          className={styles.input}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('ai:chatPlaceholder')}
          rows={1}
          disabled={loading}
        />
        <button
          className={styles.sendBtn}
          onClick={() => handleSend()}
          disabled={!input.trim() || loading}
        >
          &#x27A4;
        </button>
      </div>
    </div>
  );
}
